/**
 * Resultado da Venda Direta: a VENDA (confirmada: estoque e financeiro lançados) e a NF-e
 * (situação acompanhada sozinha) aparecem separadas — venda confirmada não é nota autorizada.
 *
 * A tela relê a situação da nota no sistema (nunca no provedor) em intervalos crescentes,
 * sem limite de tempo enquanto a nota ainda vai mudar; o envio e as consultas à SEFAZ são do
 * worker, no servidor, e continuam com a página fechada. Ao autorizar: abre o DANFE e baixa
 * o PDF e o XML, conforme as preferências (com botões para fazer à mão).
 */
import { SALES_ORIGIN_LABEL, formatMoney } from "@cafegestor/shared";
import { Link } from "@tanstack/react-router";
import { Icon } from "@/components/icons";
import { Alert, Button, StatusBadge } from "@/components/ui";
import { qty } from "../estoque/lib";
import { DocumentDetail } from "../fiscal/DocumentDetail";
import { EmissionCard } from "../fiscal/EmissionCard";
import { useEmissionTracking } from "../fiscal/emission-tracking";
import type { AutoPrefs, DirectSaleOutcomeData } from "./direct-sale-session";

export function DirectSaleOutcome({
  result,
  prefs,
  pendingTab,
  onNew,
}: {
  result: DirectSaleOutcomeData;
  prefs: AutoPrefs;
  /** guia preparada no clique de "Finalizar" (null: bloqueada, recusada ou página recarregada) */
  pendingTab: Window | null;
  onNew: () => void;
}) {
  const track = useEmissionTracking({
    documentId: result.documentId,
    finishedAt: result.finishedAt,
    prefs,
    pendingTab,
  });
  const d = track.doc;
  const stage = track.stage;
  const finished = new Date(result.finishedAt).toLocaleString("pt-BR", {
    dateStyle: "short",
    timeStyle: "short",
  });

  return (
    <div className="space-y-5">
      <section
        aria-labelledby="venda-confirmada"
        className="rounded-xl border border-success/30 bg-card shadow-card"
      >
        <div className="flex flex-wrap items-start gap-4 px-5 py-5">
          <span
            aria-hidden
            className="flex size-12 shrink-0 items-center justify-center rounded-full bg-success-soft text-success"
          >
            <Icon name="check" size={26} strokeWidth={2.4} />
          </span>
          <div className="min-w-0 flex-1">
            <h2 id="venda-confirmada" className="text-xl font-semibold">
              Venda confirmada com sucesso
            </h2>
            <p className="text-sm text-muted-foreground">
              {SALES_ORIGIN_LABEL.venda_direta}: estoque e financeiro lançados.
              {result.replayed && " Esta venda já tinha sido finalizada — nada foi repetido."}
            </p>
          </div>
          <div className="text-right">
            <p className="text-xs font-medium text-muted-foreground">Valor total</p>
            <p className="text-[28px] font-bold leading-tight tabular-nums text-primary">
              {formatMoney(result.totalAmount)}
            </p>
          </div>
        </div>
        <dl className="grid grid-cols-2 gap-x-6 gap-y-3 border-t border-border/70 px-5 py-4 sm:grid-cols-4">
          <div className="min-w-0">
            <dt className="text-xs font-medium text-muted-foreground">Pedido</dt>
            <dd className="mt-0.5 font-semibold">{result.orderNumber}</dd>
          </div>
          <div className="col-span-2 min-w-0 sm:col-span-1">
            <dt className="text-xs font-medium text-muted-foreground">Cliente</dt>
            <dd className="mt-0.5 text-sm font-medium break-words">
              {result.customerLabel || d?.recipientName || "—"}
            </dd>
          </div>
          <div className="min-w-0">
            <dt className="text-xs font-medium text-muted-foreground">Data e hora</dt>
            <dd className="mt-0.5 text-sm font-medium">{finished}</dd>
          </div>
          <div className="min-w-0">
            <dt className="text-xs font-medium text-muted-foreground">Situação da venda</dt>
            <dd className="mt-1">
              <StatusBadge tone="ok">Confirmada</StatusBadge>
            </dd>
          </div>
        </dl>
      </section>

      {result.shortages.length > 0 && (
        <section
          aria-labelledby="pendencia-estoque"
          className="rounded-xl border border-warning/30 bg-warning-soft px-5 py-4 text-warning-strong"
        >
          <h2 id="pendencia-estoque" className="flex items-center gap-2 font-semibold">
            <Icon name="estoque" size={18} />
            Venda confirmada com pendência de regularização de estoque.
          </h2>
          <ul className="mt-2 space-y-0.5 text-sm">
            {result.shortages.map((s) => (
              <li key={s.productId}>
                <strong>{s.productCode}</strong>: {qty(s.short, s.baseUnitCode)} a regularizar
                (vendido {qty(s.needed, s.baseUnitCode)}, disponível{" "}
                {qty(s.available, s.baseUnitCode)})
              </li>
            ))}
          </ul>
          <p className="mt-2 text-sm">
            Isto não afeta a nota fiscal.{" "}
            <Link to="/estoque/pendencias" className="font-semibold underline underline-offset-2">
              Estoque → Pendências de estoque
            </Link>
          </p>
        </section>
      )}

      {d && stage ? (
        <EmissionCard doc={d} stage={stage} steps={track.steps} delivery={track.delivery} />
      ) : track.error ? (
        <Alert tone="warn">
          Não foi possível ler a situação da NF-e agora (série {result.fiscalSeries} nº{" "}
          {result.fiscalNumber}). A emissão continua no servidor; a tela tenta de novo sozinha.
        </Alert>
      ) : (
        <p role="status" className="text-sm text-muted-foreground">
          Carregando a situação da NF-e série {result.fiscalSeries} nº {result.fiscalNumber}…
        </p>
      )}

      <DocumentDetail id={result.documentId} variant="resultado" />

      <div className="flex flex-wrap gap-2">
        <Button size="lg" onClick={onNew}>
          <Icon name="plus" size={18} strokeWidth={2.2} />
          Nova Venda Direta
        </Button>
      </div>
    </div>
  );
}
